import { useState } from "react";
import { NavLink, Link, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABELS } from "../lib/constants";
import {
  LayoutDashboard,
  ArrowDownToLine,
  ArrowUpFromLine,
  Boxes,
  FileBarChart2,
  Users,
  LogOut,
  HandCoins,
  Menu,
  X,
  Database,
  Ambulance,
} from "lucide-react";

const FINANCE = ["admin", "pengurus", "operator"];
const LAYANAN_ROLES = ["admin", "pengurus", "operator", "koordinator_layanan", "driver"];

const NAV = [
  { to: "/dashboard", label: "Dasbor", icon: LayoutDashboard, testid: "nav-dashboard", roles: FINANCE },
  { to: "/penerimaan", label: "Penerimaan", icon: ArrowDownToLine, testid: "nav-penerimaan", roles: FINANCE },
  { to: "/pentasyarufan", label: "Pentasyarufan", icon: ArrowUpFromLine, testid: "nav-pentasyarufan", roles: FINANCE },
  { to: "/aset", label: "Aset Kelolaan", icon: Boxes, testid: "nav-aset", roles: FINANCE },
  { to: "/database", label: "Database", icon: Database, testid: "nav-database", roles: FINANCE },
  { to: "/layanan", label: "Permohonan Layanan", icon: Ambulance, testid: "nav-layanan", roles: LAYANAN_ROLES },
  { to: "/laporan", label: "Laporan", icon: FileBarChart2, testid: "nav-laporan", roles: FINANCE },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  const navItems = NAV.filter((n) => n.roles.includes(user?.role));
  if (user?.role === "admin") {
    navItems.push({ to: "/pengguna", label: "Pengguna", icon: Users, testid: "nav-pengguna" });
  }

  const sidebar = (
    <div className="flex h-full flex-col bg-white border-r border-slate-200">
      <Link to="/" className="flex items-center gap-3 px-5 py-5 border-b border-slate-200 transition-colors duration-200 hover:bg-slate-50" data-testid="sidebar-logo-link">
        <img src="/logo-lazisnu.png" alt="Logo LAZISNU Dawe" className="h-11 w-11 rounded-lg bg-white object-contain p-0.5" />
        <div>
          <p className="font-heading font-bold text-slate-900 leading-tight">LAZISNU Dawe</p>
          <p className="text-xs text-slate-500">Sistem Informasi PSAK 109</p>
        </div>
      </Link>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={item.testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
                isActive
                  ? "bg-emerald-800 text-white"
                  : "text-slate-600 hover:bg-emerald-50 hover:text-emerald-900"
              }`
            }
          >
            <item.icon className="h-4 w-4" strokeWidth={1.8} />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-slate-200 p-4">
        <div className="mb-3">
          <p className="text-sm font-semibold text-slate-800 truncate" data-testid="sidebar-user-name">{user?.name}</p>
          <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          <span className="mt-1 inline-block rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800" data-testid="sidebar-user-role">
            {ROLE_LABELS[user?.role] || user?.role}
          </span>
        </div>
        <button
          data-testid="logout-button"
          onClick={logout}
          className="flex w-full items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 transition-colors duration-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
        >
          <LogOut className="h-4 w-4" strokeWidth={1.8} />
          Keluar
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 lg:block">{sidebar}</aside>

      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 shadow-xl">{sidebar}</aside>
        </div>
      )}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-slate-200 bg-white px-4 py-3 lg:hidden">
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="rounded-lg border border-slate-200 p-2 text-slate-600"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <Link to="/" className="flex items-center gap-2" data-testid="mobile-logo-link">
            <img src="/logo-lazisnu.png" alt="Logo LAZISNU Dawe" className="h-7 w-7 rounded bg-white object-contain" />
            <span className="font-heading font-bold text-slate-900">LAZISNU Dawe</span>
          </Link>
        </header>
        <main className="p-4 md:p-6 lg:p-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
